import { useState, useEffect, useCallback } from 'react';
import { generateClient } from 'aws-amplify/data';
import { getUrl } from 'aws-amplify/storage';
import type { Schema } from '../../amplify/data/resource';
import type { Tour } from '../types';

const getClient = () => {
  try {
    return generateClient<Schema>();
  } catch {
    return null;
  }
};

interface UseToursResult {
  tours: Tour[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useTours(): UseToursResult {
  const [tours, setTours] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchTours = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const client = getClient();
      if (!client) {
        setTours([]);
        setLoading(false);
        return;
      }
      const { data } = await client.models.Tour.list();

      // Filter for active tours client-side and deduplicate by slug
      const activeTours = data.filter((tour) => tour.isActive !== false);
      const seenSlugs = new Set<string>();
      const uniqueData = activeTours.filter((tour) => {
        if (seenSlugs.has(tour.slug)) {
          return false;
        }
        seenSlugs.add(tour.slug);
        return true;
      });

      // Convert Amplify tours to the existing Tour interface format
      const resolvedTours = await Promise.all(
        uniqueData.map(async (tour) => {
          // Resolve card image URL
          let cardImage = '';
          if (tour.cardImageKey) {
            try {
              const result = await getUrl({ path: tour.cardImageKey });
              cardImage = result.url.toString();
            } catch (err) {
              console.error('Error loading card image:', err);
            }
          }

          // Resolve slide images
          const slideImages: Record<string, string> & { name?: string; numKeys?: number } = {};
          if (tour.slideImageKeys && tour.slideImageKeys.length > 0) {
            for (let i = 0; i < tour.slideImageKeys.length; i++) {
              const key = tour.slideImageKeys[i];
              if (key) {
                try {
                  const result = await getUrl({ path: key });
                  slideImages[`${tour.slug}${i + 1}.webp`] = result.url.toString();
                } catch (err) {
                  console.error('Error loading slide image:', err);
                }
              }
            }
            slideImages.name = tour.slug;
            slideImages.numKeys = tour.slideImageKeys.length;
          }

          // Resolve grid images
          const gridImages: Record<string, string> = {};
          if (tour.gridImageKeys && tour.gridImageKeys.length > 0) {
            for (let i = 0; i < tour.gridImageKeys.length; i++) {
              const key = tour.gridImageKeys[i];
              if (key) {
                try {
                  const result = await getUrl({ path: key });
                  gridImages[`grid${i + 1}.webp`] = result.url.toString();
                } catch (err) {
                  console.error('Error loading grid image:', err);
                }
              }
            }
          }

          // Convert to existing Tour interface
          const convertedTour: Tour = {
            name: tour.slug,
            text: tour.name,
            ShortDesc: tour.shortDescription,
            content: tour.content || [],
            whatToBring: tour.whatToBring || [],
            includes: tour.includes || [],
            price: tour.price ?? undefined,
            priceChild: tour.priceChild || 0,
            cruise: tour.cruise || false,
            cayes: tour.cayes || false,
            notes: tour.notes || undefined,
            url: tour.urlPath,
            cardImage,
            slideImages,
            gridImages,
          };

          return {
            tour: convertedTour,
            popularity: tour.popularity || 0,
          };
        })
      );

      // Sort by popularity descending
      resolvedTours.sort((a, b) => b.popularity - a.popularity);

      setTours(resolvedTours.map((t) => t.tour));
    } catch (err) {
      console.error('Error fetching tours:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTours();
  }, [fetchTours]);

  return { tours, loading, error, refetch: fetchTours };
}
